import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  CreateDataConnectionInput,
  DataConnection,
  UpdateDataConnectionInput,
} from "@/api/data-connections-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"

const CONNECTION_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{1,63}$/

type Mode = "create" | "edit"

type FormState = {
  id: string
  name: string
  host: string
  port: string
  database: string
  username: string
  password: string
  enabled: boolean
}

type Errors = Partial<Record<keyof FormState, string>>

type AddDataConnectionDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: CreateDataConnectionInput | UpdateDataConnectionInput) => Promise<void>
  isPending: boolean
  /** Present in edit mode — the existing record to pre-fill. */
  existing?: DataConnection | null
}

const EMPTY: FormState = {
  id: "",
  name: "",
  host: "",
  port: "9030",
  database: "",
  username: "",
  password: "",
  enabled: true,
}

export function AddDataConnectionDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  isPending,
  existing,
}: AddDataConnectionDialogProps) {
  const { t } = useTranslation()
  const mode: Mode = existing ? "edit" : "create"

  const initial = useMemo<FormState>(() => {
    if (!existing) return EMPTY
    return {
      id: existing.id,
      name: existing.name,
      host: existing.host,
      port: String(existing.port),
      database: existing.database,
      username: existing.username,
      password: "",
      enabled: existing.enabled,
    }
  }, [existing])

  const [form, setForm] = useState<FormState>(initial)
  const [errors, setErrors] = useState<Errors>({})

  useEffect(() => {
    if (isOpen) {
      setForm(initial)
      setErrors({})
    }
  }, [isOpen, initial])

  const update = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }))

  const validate = (): boolean => {
    const next: Errors = {}
    if (mode === "create") {
      const id = form.id.trim().toLowerCase()
      if (!id) {
        next.id = t("adminDashboard.dataConnections.addDialog.errors.idRequired", "ID is required")
      } else if (!CONNECTION_ID_PATTERN.test(id)) {
        next.id = t(
          "adminDashboard.dataConnections.addDialog.errors.idFormat",
          "ID must be lowercase letters, digits, '-' or '_' (2–64 chars)"
        )
      }
    }
    if (!form.name.trim()) {
      next.name = t(
        "adminDashboard.dataConnections.addDialog.errors.nameRequired",
        "Name is required"
      )
    }
    if (!form.host.trim()) {
      next.host = t(
        "adminDashboard.dataConnections.addDialog.errors.hostRequired",
        "Host is required"
      )
    }
    const port = Number(form.port)
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      next.port = t(
        "adminDashboard.dataConnections.addDialog.errors.portRange",
        "Port must be between 1 and 65535"
      )
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) return

    if (mode === "create") {
      const payload: CreateDataConnectionInput = {
        id: form.id.trim().toLowerCase(),
        name: form.name.trim(),
        driver_type: "pymysql",
        host: form.host.trim(),
        port: Number(form.port),
        database: form.database.trim() || undefined,
        username: form.username.trim() || undefined,
        password: form.password || undefined,
        enabled: form.enabled,
      }
      await onSubmit(payload)
    } else {
      const payload: UpdateDataConnectionInput = {
        name: form.name.trim(),
        host: form.host.trim(),
        port: Number(form.port),
        database: form.database.trim(),
        username: form.username.trim(),
        enabled: form.enabled,
      }
      if (form.password) payload.password = form.password
      await onSubmit(payload)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? t("adminDashboard.dataConnections.addDialog.title", "Add data connection")
              : t("adminDashboard.dataConnections.addDialog.editTitle", "Edit data connection")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.dataConnections.addDialog.description",
              "Apache Doris / MySQL-compatible SQL connection for the Dash agent. Passwords are stored in Key Vault — never in the database record."
            )}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          {mode === "create" && (
            <div className="space-y-2">
              <Label htmlFor="dc-id">
                {t("adminDashboard.dataConnections.addDialog.id", "Connection ID")}
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="dc-id"
                placeholder="shl-doris-c01"
                value={form.id}
                onChange={(e) => update({ id: e.target.value })}
              />
              {errors.id && <p className="text-destructive text-sm">{errors.id}</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="dc-name">
              {t("adminDashboard.dataConnections.addDialog.name", "Display name")}
              <span className="text-destructive"> *</span>
            </Label>
            <Input
              id="dc-name"
              placeholder="SHL Doris"
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
            />
            {errors.name && <p className="text-destructive text-sm">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_120px]">
            <div className="space-y-2">
              <Label htmlFor="dc-host">
                {t("adminDashboard.dataConnections.addDialog.host", "Host")}
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="dc-host"
                placeholder="shl-dlh-c01.ad.blue-cloud.se"
                value={form.host}
                onChange={(e) => update({ host: e.target.value })}
              />
              {errors.host && <p className="text-destructive text-sm">{errors.host}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dc-port">
                {t("adminDashboard.dataConnections.addDialog.port", "Port")}
                <span className="text-destructive"> *</span>
              </Label>
              <Input
                id="dc-port"
                max={65535}
                min={1}
                type="number"
                value={form.port}
                onChange={(e) => update({ port: e.target.value })}
              />
              {errors.port && <p className="text-destructive text-sm">{errors.port}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dc-database">
              {t("adminDashboard.dataConnections.addDialog.database", "Database")}
            </Label>
            <Input
              id="dc-database"
              value={form.database}
              placeholder={t(
                "adminDashboard.dataConnections.addDialog.databaseHint",
                "Leave empty to list databases with SHOW DATABASES"
              )}
              onChange={(e) => update({ database: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dc-username">
              {t("adminDashboard.dataConnections.addDialog.username", "Username")}
            </Label>
            <Input
              autoComplete="off"
              id="dc-username"
              value={form.username}
              onChange={(e) => update({ username: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dc-password">
              {t("adminDashboard.dataConnections.addDialog.password", "Password")}
            </Label>
            <Input
              autoComplete="new-password"
              id="dc-password"
              type="password"
              value={form.password}
              placeholder={
                mode === "edit"
                  ? t(
                      "adminDashboard.dataConnections.addDialog.passwordEditHint",
                      "Leave empty to keep the current password"
                    )
                  : t(
                      "adminDashboard.dataConnections.addDialog.passwordCreateHint",
                      "Leave empty to bind to an existing Key Vault secret"
                    )
              }
              onChange={(e) => update({ password: e.target.value })}
            />
            {errors.password && <p className="text-destructive text-sm">{errors.password}</p>}
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">
                {t("adminDashboard.dataConnections.addDialog.enabled", "Enabled")}
              </p>
              <p className="text-muted-foreground text-xs">
                {t(
                  "adminDashboard.dataConnections.addDialog.enabledHint",
                  "Disabled connections stay configured but the Dash agent will not use them."
                )}
              </p>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(checked) => update({ enabled: checked })}
            />
          </div>

          <DialogFooter>
            <Button
              disabled={isPending}
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button disabled={isPending} type="submit">
              {isPending && <Spinner className="mr-2 size-4" />}
              {mode === "create"
                ? t("adminDashboard.dataConnections.addDialog.submit", "Add connection")
                : t("adminDashboard.dataConnections.addDialog.submitEdit", "Save changes")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
